// Copied verbatim from
// pinpointedu/frontend/apps/product/src/next/app/routes/renderRouteManifest.tsx
// on 2026-09-05.
import {
  createElement,
  type ComponentType,
  type ReactNode,
} from 'react';
import { Route } from 'react-router-dom';

export type RouteManifestNode = {
  id: string;
  path?: string;
  index?: boolean;
  component?: string;
  guard?: string;
  children?: RouteManifestNode[];
};

type BoundRouteManifestNode = RouteManifestNode & {
  element: ReactNode;
  children?: BoundRouteManifestNode[];
};

export type RouteBinding = {
  component: ComponentType<any>;
  getProps: (context: Record<string, unknown>) => Record<string, unknown>;
  transformElement: null | ((element: ReactNode, context: Record<string, unknown>) => ReactNode);
};

export function defineRouteBinding(
  component: ComponentType<any>,
  getProps: (context: Record<string, unknown>) => Record<string, unknown> = () => ({}),
  transformElement: null | ((element: ReactNode, context: Record<string, unknown>) => ReactNode) = null,
): RouteBinding {
  return { component, getProps, transformElement };
}

export function bindRouteManifest(
  nodes: RouteManifestNode[],
  bindings: Record<string, RouteBinding>,
  guardComponents: Record<string, ComponentType<any>>,
  context: Record<string, unknown> = {},
  elementFactory: (component: ComponentType<any>, props: Record<string, unknown>, ...children: ReactNode[]) => ReactNode = createElement,
): BoundRouteManifestNode[] {
  if (!Array.isArray(nodes)) throw new Error('route manifest must be an array');
  return nodes.map((node) => {
    const binding = bindings[node.id];
    if (!binding) throw new Error(`missing route binding for ${node.id}`);
    let element = elementFactory(binding.component, binding.getProps(context));
    if (binding.transformElement) element = binding.transformElement(element, context);
    if (node.guard && node.guard !== 'none') {
      const Guard = guardComponents[node.guard];
      if (!Guard) throw new Error(`missing guard component ${node.guard} for ${node.id}`);
      element = elementFactory(Guard, {}, element);
    }
    return {
      ...node,
      element,
      children: node.children
        ? bindRouteManifest(
            node.children,
            bindings,
            guardComponents,
            context,
            elementFactory,
          )
        : undefined,
    };
  });
}

export function renderRouteManifest(
  nodes: RouteManifestNode[],
  bindings: Record<string, RouteBinding>,
  guardComponents: Record<string, ComponentType<any>>,
  context: Record<string, unknown> = {},
): ReactNode[] {
  const bound = bindRouteManifest(nodes, bindings, guardComponents, context);
  const render = (routeNodes: BoundRouteManifestNode[]): ReactNode[] => routeNodes.map((node) => {
    const props: Record<string, unknown> = { key: node.id, element: node.element };
    if (node.index) props.index = true;
    else if (node.path !== undefined) props.path = node.path;
    const children = node.index || !node.children ? undefined : render(node.children);
    return createElement(Route, props, children);
  });
  return render(bound);
}
